import { useState } from 'react';
import { Icon, SimulatedTag, StatusDot } from '@hive/ui';
import type { Entitlement, Programme, Screen } from '../api/client';
import { venueTime } from '../lib/format';

function expiryLabel(e: Entitlement): string {
  if (e.status === 'expired') return 'Expired';
  if (e.status === 'expiring') return `Expires in ${e.daysLeft} day${e.daysLeft === 1 ? '' : 's'}`;
  return `Renews ${new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(e.validTo))}`;
}

export function EntitlementsCard({ entitlements }: { entitlements: Entitlement[] | null }) {
  const [note, setNote] = useState(false);
  const list = entitlements ?? [];
  const expiring = list.filter((e) => e.status !== 'active').length;
  return (
    <section aria-label="Entitlements" className="k-card k-panel">
      <div className="k-panel__head">
        <div className="k-panel__title">
          <span aria-hidden="true" className="k-icon-round">
            <Icon name="licence" size={18} />
          </span>
          <span className="k-panel__name">Entitlements</span>
          <span className="hv-muted">
            · {list.length} competition{list.length === 1 ? '' : 's'}
            {expiring ? ` · ${expiring} expiring soon` : ''}
          </span>
        </div>
        <button type="button" className="hv-btn hv-btn--link hv-btn--sm" onClick={() => setNote((n) => !n)}>
          Manage
        </button>
      </div>
      {note && <p className="hv-muted k-panel__note">Rights are managed by your account manager. Renewals are not connected in this prototype.</p>}
      <div className="k-ents">
        {list.map((e) => (
          <div key={e.id} className={`k-ent${e.status !== 'active' ? ' k-ent--warn' : ''}`}>
            <div className="k-ent__text">
              <span className="k-ent__name">{e.competition}</span>
              <span className="k-ent__when">{expiryLabel(e)}</span>
            </div>
            {e.status === 'active' ? (
              <StatusDot status="healthy" />
            ) : (
              <button type="button" className="hv-btn hv-btn--primary hv-btn--sm" onClick={() => setNote(true)}>
                Renew
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

export function TonightCard({ programme, screens, timezone, onPromo }: { programme: Programme | null; screens: Screen[]; timezone?: string; onPromo(): void }) {
  const items = programme?.upcoming ?? [];
  const where = (fixtureId: string) => screens.find((s) => s.nowPlaying?.fixtureId === fixtureId)?.name;
  return (
    <section aria-label="Tonight's schedule" className="k-card k-panel">
      <div className="k-panel__head">
        <span className="k-panel__name">Tonight</span>
        <button type="button" className="hv-btn hv-btn--outline-accent hv-btn--sm" onClick={onPromo}>
          <Icon name="megaphone" size={16} />
          Nectar promo
        </button>
      </div>
      <div className="k-tonight">
        {items.length === 0 && <span className="hv-muted">No fixtures tonight</span>}
        {items.map((i) => (
          <div key={i.fixtureId} className="k-tonight__row">
            <span className="k-tonight__time">
              {i.status === 'live' ? (
                <>
                  <StatusDot status="live" small />
                  {i.clock ?? 'Live'}
                </>
              ) : (
                venueTime(i.startsAt, timezone)
              )}
            </span>
            <span className="k-tonight__title">
              {i.title}
              {i.status === 'live' && i.clock && i.scoreSource === 'simulated' && <SimulatedTag title="Match clock is simulated" />}
            </span>
            <span className="hv-muted">{where(i.fixtureId) ?? i.competitionShort ?? ''}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
